import React, { useState } from "react";

/**
 * Composant simplifié pour une demi-droite graduée
 * - Affiche par défaut uniquement l'origine et la fin
 * - L'utilisateur peut masquer ces valeurs
 * - L'utilisateur peut ajouter des points (A, B...) avec valeur visible ou cachée
 * - Supporte les fractions (ex: 2/5, 3/4)
 */

// ===== COMPOSANT FRACTION =====
const FractionDisplay = ({ numerator, denominator, className = "" }) => (
    <span className={`inline-flex flex-col items-center leading-none ${className}`}>
        <span className="border-b border-current px-0.5">{numerator}</span>
        <span className="px-0.5">{denominator}</span>
    </span>
);

// ===== PARSER FRACTION/NOMBRE =====
const parseFractionOrNumber = (input) => {
    if (!input || input === '') return null;

    const str = String(input).trim();

    // Vérifier si c'est une fraction (contient /)
    if (str.includes('/')) {
        const parts = str.split('/');
        if (parts.length !== 2) return null;

        const num = parseFloat(parts[0].trim());
        const den = parseFloat(parts[1].trim());

        if (isNaN(num) || isNaN(den) || den === 0) return null;

        return {
            value: num / den,
            displayFraction: { num: Math.round(num), den: Math.round(den) }
        };
    }

    // Sinon c'est un nombre normal
    const value = parseFloat(str);
    if (isNaN(value)) return null;

    return { value, displayFraction: null };
};

function NumberLineElement({ config, onConfigChange, onRemove, isPreview = false }) {
    // ===== CONFIGURATION PAR DÉFAUT =====
    const defaultConfig = {
        start: 0,
        end: 10,
        step: 1,
        lineType: 'half',     // 'half' = demi-droite, 'full' = droite (avec extension gauche)
        showArrow: true,
        showStart: true,      // Afficher le nombre à l'origine
        showEnd: true,        // Afficher le nombre à la fin
        label: '',
        points: [],           // Points : { value, label, showValue, color }
    };

    const currentConfig = { ...defaultConfig, ...config };

    // Nombre maximum de graduations autorisées (performance)
    const MAX_GRADUATIONS = 101;

    // ===== ÉTATS LOCAUX =====
    const [showPointsPanel, setShowPointsPanel] = useState(false);
    const [newPoint, setNewPoint] = useState({
        value: '',
        label: '',
        showValue: false,
        color: '#475569'
    });

    // ===== HELPERS =====
    const updateConfig = (key, value) => {
        onConfigChange && onConfigChange({ ...currentConfig, [key]: value });
    };

    const getPoints = () => Array.isArray(currentConfig.points) ? currentConfig.points : [];

    // ===== CALCUL DES GRADUATIONS (avec protection anti-surcharge) =====
    const getTheoreticalCount = () => {
        const { start, end, step } = currentConfig;
        if (step <= 0 || start >= end) return 0;
        return Math.ceil((end - start) / step) + 1;
    };

    const getMinStep = () => {
        const { start, end } = currentConfig;
        if (start >= end) return 1;
        return (end - start) / (MAX_GRADUATIONS - 1);
    };

    const theoreticalCount = getTheoreticalCount();
    const isTooManyGraduations = theoreticalCount > MAX_GRADUATIONS;
    const minStep = getMinStep();

    const getGraduations = () => {
        const { start, end, step } = currentConfig;
        if (step <= 0 || start >= end) return [];

        const graduations = [];
        const effectiveStep = isTooManyGraduations ? minStep : step;

        for (let i = start; i <= end; i += effectiveStep) {
            graduations.push(Math.round(i * 1000) / 1000);
            if (graduations.length >= MAX_GRADUATIONS) break;
        }
        return graduations;
    };

    const graduations = getGraduations();

    // ===== GESTION DES POINTS =====
    const addPoint = () => {
        const parsed = parseFractionOrNumber(newPoint.value);
        if (!parsed) return;

        const { value: pointValue, displayFraction } = parsed;
        const { start, end } = currentConfig;
        if (pointValue < start || pointValue > end) return;

        const points = getPoints();
        const existingIdx = points.findIndex(p => Math.abs(p.value - pointValue) < 0.001);

        const pointData = {
            value: pointValue,
            displayFraction, // { num, den } ou null
            label: newPoint.label.trim() || '',
            showValue: newPoint.showValue,
            color: newPoint.color
        };

        const newPoints = existingIdx >= 0
            ? points.map((p, i) => i === existingIdx ? pointData : p)
            : [...points, pointData];

        updateConfig('points', newPoints);
        setNewPoint({ value: '', label: '', showValue: false, color: '#475569' });
    };

    const removePoint = (index) => {
        updateConfig('points', getPoints().filter((_, i) => i !== index));
    };

    const findPoint = (value) => getPoints().find(p => Math.abs(p.value - value) < 0.001);

    // Helper pour afficher une valeur (fraction ou décimal)
    const renderValue = (point) => {
        if (point?.displayFraction) {
            return (
                <FractionDisplay
                    numerator={point.displayFraction.num}
                    denominator={point.displayFraction.den}
                />
            );
        }
        const val = point?.value ?? point;
        return Number.isInteger(val) ? val : parseFloat(val.toPrecision(10));
    };

    // ===== COULEURS DISPONIBLES =====
    const colors = [
        { value: '#475569', label: 'Standard' },
        { value: '#3b82f6', label: 'Bleu' },
        { value: '#ef4444', label: 'Rouge' },
        { value: '#22c55e', label: 'Vert' },
        { value: '#f59e0b', label: 'Orange' },
        { value: '#8b5cf6', label: 'Violet' },
    ];

    // ===== HELPERS SVG =====
    const formatValue = (val) => Number.isInteger(val) ? String(val) : val.toFixed(1);

    // Rendu d'une valeur (fraction ou nombre) en SVG
    const renderSvgValue = (point, x, y) => {
        if (point?.displayFraction) {
            return (
                <foreignObject x={x - 14} y={y - 2} width={28} height={24} key={`frac-${x}`}>
                    <div xmlns="http://www.w3.org/1999/xhtml" style={{ display: 'flex', justifyContent: 'center' }}>
                        <FractionDisplay
                            numerator={point.displayFraction.num}
                            denominator={point.displayFraction.den}
                            className="text-[10px] text-slate-600"
                        />
                    </div>
                </foreignObject>
            );
        }
        const val = point?.value ?? point;
        return (
            <text x={x} y={y + 9} textAnchor="middle" className="text-[10px] fill-slate-600">
                {Number.isInteger(val) ? val : parseFloat(val.toPrecision(10))}
            </text>
        );
    };

    // ===== RENDU SVG DE LA DROITE GRADUÉE =====
    const renderNumberLine = () => {
        const { start, end, showArrow, showStart, showEnd, lineType } = currentConfig;
        const isFullLine = lineType === 'full';

        // Dimensions du viewBox
        const W = 600;
        const H = 90;
        const lineY = 40;
        const padL = isFullLine ? 40 : 25;
        const padR = showArrow ? 35 : 15;
        const usableW = W - padL - padR;

        // Convertir une valeur en coordonnée X
        const toX = (val) => padL + ((val - start) / (end - start)) * usableW;

        // Taille des traits
        const tickSmall = 4;
        const tickBig = 6;
        // Le rond d'un point : même règle que l'écran élève (NumberLineElementEleve)
        const RAYON_POINT = 4;

        return (
            <div className="w-full">
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
                    {/* Extension gauche (droite complète) */}
                    {isFullLine && (
                        <line x1={padL - 18} y1={lineY} x2={padL} y2={lineY} stroke="#475569" strokeWidth={1} />
                    )}

                    {/* Ligne principale */}
                    <line x1={padL} y1={lineY} x2={W - padR + (showArrow ? 8 : 0)} y2={lineY} stroke="#475569" strokeWidth={1} />

                    {/* Flèche droite */}
                    {showArrow && (
                        <polygon
                            points={`${W - padR + 15},${lineY} ${W - padR + 6},${lineY - 4} ${W - padR + 6},${lineY + 4}`}
                            fill="#475569"
                        />
                    )}

                    {/* Graduations */}
                    {graduations.map((value, index) => {
                        const x = toX(value);
                        const isFirst = index === 0;
                        const isLast = index === graduations.length - 1;
                        const point = findPoint(value);
                        const isMajor = isFirst || isLast || !!point;
                        const tH = isMajor ? tickBig : tickSmall;
                        const color = point?.color || '#475569';

                        const shouldShowNumber =
                            (isFirst && showStart) ||
                            (isLast && showEnd) ||
                            (point && point.showValue);

                        return (
                            <g key={index}>
                                {/* Trait de graduation */}
                                <line x1={x} y1={lineY - tH} x2={x} y2={lineY + tH} stroke={color} strokeWidth={1} />

                                {/* Un point, c'est un rond, étiquette ou pas */}
                                {point && <circle cx={x} cy={lineY} r={RAYON_POINT} fill={color} />}
                                {point?.label && (
                                    <text x={x} y={lineY - tickBig - 6} textAnchor="middle" fill={color} className="text-[10px] font-bold">
                                        {point.label}
                                    </text>
                                )}

                                {/* Valeur en-dessous */}
                                {shouldShowNumber && (
                                    point
                                        ? renderSvgValue(point, x, lineY + tH + 4)
                                        : <text x={x} y={lineY + tH + 14} textAnchor="middle" className="text-[10px] fill-slate-600">{formatValue(value)}</text>
                                )}
                            </g>
                        );
                    })}

                    {/* Points hors graduations */}
                    {getPoints()
                        .filter(p => !graduations.some(g => Math.abs(g - p.value) < 0.001))
                        .map((point, idx) => {
                            const relativePos = (point.value - start) / (end - start);
                            if (relativePos < 0 || relativePos > 1) return null;
                            const x = toX(point.value);

                            // Un rond, jamais un trait : un trait ici serait une
                            // graduation de plus, au mauvais endroit (cf. l'écran élève).
                            return (
                                <g key={`extra-${idx}`}>
                                    <circle cx={x} cy={lineY} r={RAYON_POINT} fill={point.color} />
                                    {point.label && (
                                        <text x={x} y={lineY - tickBig - 6} textAnchor="middle" fill={point.color} className="text-[10px] font-bold">
                                            {point.label}
                                        </text>
                                    )}
                                    {point.showValue && renderSvgValue(point, x, lineY + tickBig + 4)}
                                </g>
                            );
                        })}
                </svg>

                {/* Légende (en HTML sous le SVG) */}
                {currentConfig.label && (
                    <p className="text-center text-sm text-slate-600 italic mt-1">{currentConfig.label}</p>
                )}
            </div>
        );
    };

    // ===== MODE APERÇU =====
    if (isPreview) {
        return (
            <div className="bg-white rounded-xl p-4 border border-slate-200">
                {renderNumberLine()}
            </div>
        );
    }

    // ===== MODE CONFIGURATION =====
    return (
        <div className="border border-sky-200 rounded-xl overflow-hidden bg-white">
            {/* Aperçu de la droite */}
            <div className={`px-3 py-1 border-b ${isTooManyGraduations ? 'border-amber-300 bg-amber-50/40' : 'border-sky-100'}`}>
                {renderNumberLine()}
                {isTooManyGraduations && (
                    <p className="text-[10px] text-amber-600 text-center pb-1">
                        Trop de graduations ({theoreticalCount.toLocaleString()}) — pas min. : {minStep.toFixed(2)}
                    </p>
                )}
            </div>

            {/* Config de base : Min / Fin / Pas */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-sky-100">
                <div className="flex items-center gap-1.5 flex-1">
                    <span className="text-[10px] font-medium text-slate-400 w-6">Min</span>
                    <input
                        type="number"
                        value={currentConfig.start}
                        onChange={(e) => updateConfig('start', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-xs text-gray-700 focus:border-sky-300 focus:outline-none transition"
                    />
                </div>
                <div className="flex items-center gap-1.5 flex-1">
                    <span className="text-[10px] font-medium text-slate-400 w-6">Fin</span>
                    <input
                        type="number"
                        value={currentConfig.end}
                        onChange={(e) => updateConfig('end', parseFloat(e.target.value) || 10)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-xs text-gray-700 focus:border-sky-300 focus:outline-none transition"
                    />
                </div>
                <div className="flex items-center gap-1.5 flex-1">
                    <span className={`text-[10px] font-medium w-6 ${isTooManyGraduations ? 'text-amber-600' : 'text-slate-400'}`}>Pas</span>
                    <input
                        type="number"
                        step="0.1"
                        min="0.1"
                        value={currentConfig.step}
                        onChange={(e) => updateConfig('step', parseFloat(e.target.value) || 1)}
                        className={`w-full px-2 py-1.5 border rounded-md text-xs focus:outline-none transition ${
                            isTooManyGraduations
                                ? 'border-amber-400 bg-amber-50 text-amber-800 focus:border-amber-500'
                                : 'border-gray-200 text-gray-700 focus:border-sky-300'
                        }`}
                    />
                </div>
            </div>

            {/* Type de ligne + Options d'affichage sur une ligne */}
            <div className="flex items-center gap-3 px-3 py-2 border-b border-sky-100 flex-wrap">
                {/* Type toggle */}
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => updateConfig('lineType', 'half')}
                        className={`px-2 py-1 rounded-md text-[10px] font-medium transition ${
                            currentConfig.lineType !== 'full'
                                ? 'bg-sky-500 text-white'
                                : 'bg-gray-50 border border-gray-200 text-gray-500 hover:border-sky-300'
                        }`}
                    >
                        Demi-droite
                    </button>
                    <button
                        onClick={() => updateConfig('lineType', 'full')}
                        className={`px-2 py-1 rounded-md text-[10px] font-medium transition ${
                            currentConfig.lineType === 'full'
                                ? 'bg-sky-500 text-white'
                                : 'bg-gray-50 border border-gray-200 text-gray-500 hover:border-sky-300'
                        }`}
                    >
                        Droite
                    </button>
                </div>

                <div className="w-px h-5 bg-gray-200" />

                {/* Checkboxes compactes */}
                <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={currentConfig.showStart}
                        onChange={(e) => updateConfig('showStart', e.target.checked)}
                        className="w-3.5 h-3.5 text-sky-600 rounded"
                    />
                    <span className="text-[11px] text-slate-600">Origine</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={currentConfig.showEnd}
                        onChange={(e) => updateConfig('showEnd', e.target.checked)}
                        className="w-3.5 h-3.5 text-sky-600 rounded"
                    />
                    <span className="text-[11px] text-slate-600">Fin</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={currentConfig.showArrow}
                        onChange={(e) => updateConfig('showArrow', e.target.checked)}
                        className="w-3.5 h-3.5 text-sky-600 rounded"
                    />
                    <span className="text-[11px] text-slate-600">Flèche</span>
                </label>
            </div>

            {/* Légende + Points collapsible */}
            <div className="px-3 py-2 space-y-2">
                {/* Légende */}
                <input
                    type="text"
                    value={currentConfig.label}
                    onChange={(e) => updateConfig('label', e.target.value)}
                    placeholder="Légende (optionnel)..."
                    className="w-full px-2.5 py-1.5 border border-gray-200 rounded-md text-xs text-gray-700 placeholder-gray-300 focus:border-sky-300 focus:outline-none transition"
                />

                {/* Section Points */}
                <div>
                    <button
                        onClick={() => setShowPointsPanel(!showPointsPanel)}
                        className="flex items-center gap-1 text-[10px] font-medium text-gray-400 hover:text-gray-600 transition"
                    >
                        <svg
                            className={`w-3 h-3 transition-transform ${showPointsPanel ? 'rotate-90' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        Points personnalisés ({getPoints().length})
                    </button>

                    {showPointsPanel && (
                        <div className="mt-2 space-y-2">
                            {/* Liste des points existants */}
                            {getPoints().length > 0 && (
                                <div className="space-y-1">
                                    {getPoints().map((point, idx) => (
                                        <div key={idx} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-gray-50 border border-gray-100">
                                            <div
                                                className="w-3 h-3 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: point.color }}
                                            />
                                            <span className="text-[11px] flex-1 text-slate-700 flex items-center gap-1">
                                                {point.label && <strong>{point.label} :</strong>}
                                                {point.displayFraction
                                                    ? <span>{point.displayFraction.num}/{point.displayFraction.den}</span>
                                                    : point.value
                                                }
                                                {point.showValue
                                                    ? <span className="text-green-600 text-[10px]">(visible)</span>
                                                    : <span className="text-orange-500 text-[10px]">(caché)</span>
                                                }
                                            </span>
                                            <button
                                                onClick={() => removePoint(idx)}
                                                className="text-red-400 hover:text-red-600 p-0.5 rounded transition"
                                            >
                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Formulaire d'ajout compact */}
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <input
                                    type="text"
                                    placeholder="Valeur (2/5, 0.4...)"
                                    value={newPoint.value}
                                    onChange={(e) => setNewPoint({ ...newPoint, value: e.target.value })}
                                    className="flex-1 min-w-[90px] px-2 py-1.5 border border-gray-200 rounded-md text-xs text-gray-700 placeholder-gray-300 focus:border-sky-300 focus:outline-none transition"
                                />
                                <input
                                    type="text"
                                    placeholder="Label"
                                    maxLength="3"
                                    value={newPoint.label}
                                    onChange={(e) => setNewPoint({ ...newPoint, label: e.target.value })}
                                    className="w-16 px-2 py-1.5 border border-gray-200 rounded-md text-xs text-gray-700 placeholder-gray-300 focus:border-sky-300 focus:outline-none transition"
                                />
                                <div className="flex items-center gap-1">
                                    {colors.map(c => (
                                        <button
                                            key={c.value}
                                            onClick={() => setNewPoint({ ...newPoint, color: c.value })}
                                            title={c.label}
                                            className={`w-5 h-5 rounded-full transition ${
                                                newPoint.color === c.value ? 'ring-2 ring-sky-400 ring-offset-1' : 'hover:scale-110'
                                            }`}
                                            style={{ backgroundColor: c.value }}
                                        />
                                    ))}
                                </div>
                                <label className="flex items-center gap-1 cursor-pointer" title="Afficher la valeur numérique">
                                    <input
                                        type="checkbox"
                                        checked={newPoint.showValue}
                                        onChange={(e) => setNewPoint({ ...newPoint, showValue: e.target.checked })}
                                        className="w-3 h-3 text-sky-600 rounded"
                                    />
                                    <span className="text-[10px] text-slate-500">Visible</span>
                                </label>
                                <button
                                    onClick={addPoint}
                                    disabled={!newPoint.value}
                                    className="px-2.5 py-1.5 bg-sky-500 text-white rounded-md text-xs font-medium hover:bg-sky-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default NumberLineElement;
